import { useEffect, useState } from "react";

import classNames from "classnames";

import { toast } from "@jti/core-components/components/Toaster";
import type { CustomError } from "@jti/fe-common";
import { isServerSide, translateBackendErrorCode } from "@jti/fe-common";
import { LoyaltyApi } from "@jti/rest-api";
import type { ScanQrActionRestrictionErrorResponse, ScanQrRestrictionCode } from "@jti/rest-api-types";
import {
  HeroContentAuthoringWrapper,
  type HeroContentAuthoringWrapperProps,
  type HeroV2Content,
  type HeroV2CtaType,
} from "@jti/ui/components/HeroV2";
import { useUser } from "@jti/ui/hooks/useUser";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import { REPLACE_URL_PLACEHOLDER, useLoginRedirectionUrl } from "../../hooks/useLoginRedirectionUrl";

type ExtendedAdditionalBehavior =
  HeroV2CtaType["additionalBehavior"] | "onlyAfterCollection" | "onlyForCooldown" | "onlyForParticipationLimitReached";

type RestrictionState = "default" | "alreadyCollected" | "cooldown" | "participationLimitReached";

type RestrictionResponse = CustomError & Partial<ScanQrActionRestrictionErrorResponse>;

export type ExtendedCta = Omit<HeroV2CtaType, "additionalBehavior"> & {
  additionalBehavior: ExtendedAdditionalBehavior;
};

export type QrCodePointsCollectionProps = Omit<HeroContentAuthoringWrapperProps["data"], "content"> & {
  content: Omit<HeroV2Content["content"], "cta"> & {
    titleCollected: string;
    textCollected: string;
    titleCooldown: string;
    textCooldown: string;
    titleParticipationLimitReached: string;
    textParticipationLimitReached: string;
    cta: ExtendedCta[] | null;
  };
  isInEditor?: boolean;
};

const RESTRICTION_ERROR_CODES: Record<
  ScanQrRestrictionCode | "loyalty.earnings::qr_code_has_been_already_read",
  Exclude<RestrictionState, "default">
> = {
  "loyalty.earnings::qr_code_has_been_already_read": "alreadyCollected",
  "loyalty.earnings::qr_code_cooldown_active": "cooldown",
  "loyalty.earnings::qr_code_participation_limit_reached": "participationLimitReached",
};

const isRestrictionErrorCode = (errorCode: string): errorCode is keyof typeof RESTRICTION_ERROR_CODES => {
  return Object.hasOwn(RESTRICTION_ERROR_CODES, errorCode);
};

const CUSTOM_CTA_BEHAVIORS = new Set<ExtendedAdditionalBehavior>([
  "onlyAfterCollection",
  "onlyForCooldown",
  "onlyForParticipationLimitReached",
]);

const isCustomCtaBehavior = (
  behavior: ExtendedAdditionalBehavior
): behavior is "onlyAfterCollection" | "onlyForCooldown" | "onlyForParticipationLimitReached" => {
  return CUSTOM_CTA_BEHAVIORS.has(behavior);
};

const convertCtaPlaceHolder = (ctas: ExtendedCta[], loginUrl: string): ExtendedCta[] => {
  return ctas.map((item) => {
    if (item.link === REPLACE_URL_PLACEHOLDER) {
      return { ...item, link: loginUrl };
    }
    return item;
  });
};

const LOGGED_IN_BEHAVIOR_BY_STATE: Record<RestrictionState, ExtendedAdditionalBehavior> = {
  default: "onlyForLoggedIn",
  alreadyCollected: "onlyAfterCollection",
  cooldown: "onlyForCooldown",
  participationLimitReached: "onlyForParticipationLimitReached",
};

const shouldShowCtaForState = (
  behavior: ExtendedAdditionalBehavior,
  isLoggedIn: boolean,
  restrictionState: RestrictionState
): boolean => {
  if (behavior === "always") {
    return true;
  }

  if (!isLoggedIn) {
    return behavior === "onlyForNotLoggedIn";
  }

  return behavior === LOGGED_IN_BEHAVIOR_BY_STATE[restrictionState];
};

const selectCtasByStateAndAuth = (
  ctas: ExtendedCta[],
  isLoggedIn: boolean,
  restrictionState: RestrictionState
): HeroV2CtaType[] => {
  return ctas.reduce<HeroV2CtaType[]>((acc, item) => {
    if (!shouldShowCtaForState(item.additionalBehavior, isLoggedIn, restrictionState)) {
      return acc;
    }

    acc.push({
      ...item,
      additionalBehavior: isCustomCtaBehavior(item.additionalBehavior) ? "onlyForLoggedIn" : item.additionalBehavior,
    });

    return acc;
  }, []);
};

const getInitialRewardId = (): string | undefined => {
  if (isServerSide()) {
    return undefined;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get("rewardId") ?? undefined;
};

const getRestrictionStateFromError = (response: RestrictionResponse): Exclude<RestrictionState, "default"> | null => {
  const { statusCode, error_code: errorCode } = response;

  if (typeof errorCode === "string" && isRestrictionErrorCode(errorCode)) {
    return RESTRICTION_ERROR_CODES[errorCode];
  }

  return statusCode === 409 ? "alreadyCollected" : null;
};

const formatNextAvailableAt = (nextAvailableAt: string | null): string => {
  if (!nextAvailableAt) {
    return "";
  }

  const parsedDate = new Date(nextAvailableAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleString();
};

const createNewContent = (
  props: QrCodePointsCollectionProps,
  restrictionState: RestrictionState,
  loginUrl: string,
  nextAvailableAt: string,
  isLoggedIn: boolean,
  earnedPoints?: number
): HeroV2Content["content"] => {
  const ctaCheckedForPlaceholder = convertCtaPlaceHolder(props.content.cta || [], loginUrl);
  const { titleLogged } = props.content;

  if (restrictionState === "alreadyCollected") {
    return {
      ...props.content,
      titleLogged: props.content.titleCollected,
      textLogged: props.content.textCollected,
      cta: selectCtasByStateAndAuth(ctaCheckedForPlaceholder, isLoggedIn, "alreadyCollected"),
    };
  }

  if (restrictionState === "cooldown") {
    return {
      ...props.content,
      titleLogged: props.content.titleCooldown,
      textLogged: replaceStringPlaceholder(props.content.textCooldown, [
        {
          placeholder: "nextAvailableAt",
          value: nextAvailableAt,
        },
      ]),
      cta: selectCtasByStateAndAuth(ctaCheckedForPlaceholder, isLoggedIn, "cooldown"),
    };
  }

  if (restrictionState === "participationLimitReached") {
    return {
      ...props.content,
      titleLogged: props.content.titleParticipationLimitReached,
      textLogged: replaceStringPlaceholder(props.content.textParticipationLimitReached, [
        {
          placeholder: "nextAvailableAt",
          value: nextAvailableAt,
        },
      ]),
      cta: selectCtasByStateAndAuth(ctaCheckedForPlaceholder, isLoggedIn, "participationLimitReached"),
    };
  }

  if (typeof earnedPoints === "number") {
    return {
      ...props.content,
      titleLogged: replaceStringPlaceholder(
        typeof titleLogged === "string" ? titleLogged : "",
        [
          {
            placeholder: "pointsEarned",
            value: earnedPoints,
          },
        ],
        false
      ),
      cta: selectCtasByStateAndAuth(ctaCheckedForPlaceholder, isLoggedIn, "default"),
    };
  }

  return {
    ...props.content,
    cta: selectCtasByStateAndAuth(ctaCheckedForPlaceholder, isLoggedIn, "default"),
  };
};

const createNewProps = (
  props: QrCodePointsCollectionProps,
  restrictionState: RestrictionState,
  loginUrl: string,
  nextAvailableAt: string,
  isLoggedIn: boolean,
  earnedPoints?: number
): HeroV2Content => {
  const newContent = createNewContent(props, restrictionState, loginUrl, nextAvailableAt, isLoggedIn, earnedPoints);
  return {
    ...props,
    content: newContent,
    isViewportHeight: false,
    isContainerWidth: true,
  };
};

export const QrCodePointsCollection: React.FC<QrCodePointsCollectionProps> = ({ isInEditor, ...props }) => {
  const { __, dictionary } = useTranslations();
  const { user, isUserLoggedIn } = useUser();
  const loginUrl = useLoginRedirectionUrl(REPLACE_URL_PLACEHOLDER);
  const initialRewardId = getInitialRewardId();
  const [rewardId] = useState<string | undefined>(initialRewardId);
  const [earnedPoints, setEarnedPoints] = useState<number | undefined>(undefined);
  const [restrictionState, setRestrictionState] = useState<RestrictionState>(
    !initialRewardId && isUserLoggedIn ? "alreadyCollected" : "default"
  );
  const [nextAvailableAt, setNextAvailableAt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const newProps = createNewProps(props, restrictionState, loginUrl, nextAvailableAt, isUserLoggedIn, earnedPoints);
  const classes = classNames("loyalty__qrCodePointsCollection", {
    "hero-banner-v2--authoring": isInEditor,
  });

  useEffect(() => {
    if (!isInEditor && isUserLoggedIn && rewardId) {
      let isMounted = true;
      setIsLoading(true);
      LoyaltyApi.init()
        .scanQrAction(rewardId, user!.ciam_token, user!.uuid)
        .then((response) => {
          if (!isMounted) return;
          setRestrictionState("default");
          setNextAvailableAt("");
          setEarnedPoints(response.earned_points);
        })
        .catch((response: RestrictionResponse) => {
          if (!isMounted) return;
          const mappedRestrictionState = getRestrictionStateFromError(response);

          if (!mappedRestrictionState) {
            const preparedError = translateBackendErrorCode(
              response?.error_code,
              __("loyalty.qrCodePointsCollection.error"),
              dictionary
            );
            toast.error(preparedError);
            setRestrictionState("alreadyCollected");
            setNextAvailableAt("");
            return;
          }

          setRestrictionState(mappedRestrictionState);
          setNextAvailableAt(formatNextAvailableAt(response?.next_available_at ?? null));
        })
        .finally(() => {
          if (!isMounted) return;
          setIsLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, rewardId]);

  return (
    <div className={classes}>
      <HeroContentAuthoringWrapper data={newProps} isLoading={isLoading} />
    </div>
  );
};
